import api from "../utils/api";

// Video Service using centralized API
const videoService = {
  // Get all videos for a prospect
  getProspectVideos: async (prospectId, filters = {}) => {
    try {
      return await api.videos.getProspectVideos(prospectId, filters);
    } catch (error) {
      throw new Error(error.message || "Failed to fetch videos");
    }
  },

  // Get single video by ID
  getVideo: async (videoId) => {
    try {
      return await api.videos.getVideo(videoId);
    } catch (error) {
      throw new Error(error.message || "Failed to fetch video");
    }
  },

  // Create new video
  createVideo: async (videoData) => {
    try {
      return await api.videos.create(videoData);
    } catch (error) {
      throw new Error(error.message || "Failed to create video");
    }
  },

  // Update video
  updateVideo: async (videoId, updateData) => {
    try {
      return await api.videos.update(videoId, updateData);
    } catch (error) {
      throw new Error(error.message || "Failed to update video");
    }
  },

  // Delete video
  deleteVideo: async (videoId) => {
    try {
      return await api.videos.delete(videoId);
    } catch (error) {
      throw new Error(error.message || "Failed to delete video");
    }
  },

  // Add timestamp note to video
  addNote: async (videoId, noteData) => {
    try {
      return await api.videos.addNote(videoId, noteData);
    } catch (error) {
      throw new Error(error.message || "Failed to add note");
    }
  },

  // Update timestamp note
  updateNote: async (videoId, noteId, updateData) => {
    try {
      return await api.videos.updateNote(videoId, noteId, updateData);
    } catch (error) {
      throw new Error(error.message || "Failed to update note");
    }
  },

  // Delete timestamp note
  deleteNote: async (videoId, noteId) => {
    try {
      return await api.videos.deleteNote(videoId, noteId);
    } catch (error) {
      throw new Error(error.message || "Failed to delete note");
    }
  },

  // Get video analytics
  getAnalytics: async () => {
    try {
      return await api.videos.getAnalytics();
    } catch (error) {
      throw new Error(error.message || "Failed to fetch analytics");
    }
  },

  // Search videos
  searchVideos: async (searchParams) => {
    try {
      return await api.videos.search(searchParams);
    } catch (error) {
      throw new Error(error.message || "Failed to search videos");
    }
  },

  // Helper function to detect video platform from URL
  detectPlatform: (url) => {
    if (!url) return "other";

    const urlLower = url.toLowerCase();
    if (urlLower.includes("youtube.com") || urlLower.includes("youtu.be")) {
      return "youtube";
    } else if (urlLower.includes("vimeo.com")) {
      return "vimeo";
    } else if (urlLower.includes("hudl.com")) {
      return "hudl";
    } else if (urlLower.includes("wistia.")) {
      return "wistia";
    } else if (urlLower.includes("dailymotion.com")) {
      return "dailymotion";
    } else if (
      urlLower.includes(".mp4") ||
      urlLower.includes(".webm") ||
      urlLower.includes(".mov")
    ) {
      return "direct";
    }
    return "other";
  },

  // Helper function to validate video URL
  validateVideoUrl: (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  },

  // Helper function to format video duration
  formatDuration: (seconds) => {
    if (!seconds || seconds < 0) return "0:00";

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
    }
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  },

  // Helper function to get platform display name
  getPlatformDisplayName: (platform) => {
    const platformNames = {
      youtube: "YouTube",
      vimeo: "Vimeo",
      hudl: "Hudl",
      direct: "Direct Video",
      wistia: "Wistia",
      dailymotion: "DailyMotion",
      other: "Other",
    };
    return platformNames[platform] || "Unknown";
  },

  // Helper function to get category display name
  getCategoryDisplayName: (category) => {
    const categoryNames = {
      highlight: "Highlight Reel",
      "game-film": "Game Film",
      training: "Training",
      interview: "Interview",
      combine: "Combine",
      skills: "Skills",
      other: "Other",
    };
    return categoryNames[category] || "Unknown";
  },

  // Helper function to get note category color
  getNoteColor: (category) => {
    const colors = {
      highlight: "#4caf50", // green
      technique: "#2196f3", // blue
      improvement: "#ff9800", // orange
      concern: "#f44336", // red
      general: "#9c27b0", // purple
    };
    return colors[category] || "#9c27b0";
  },
};

export default videoService;
